import { useEffect, useRef, useState } from "react";
import { TIME_BACKGROUNDS, getTimeOfDay } from "../lib/constants";

/**
 * Adaptive full-viewport background.
 *  - If `image` is provided, uses it directly (cross-fade on change).
 *  - Otherwise tracks time-of-day and cross-fades when crossing into a
 *    new window (dawn/day/dusk/night) while the user keeps the app open.
 *  - Always layers a dark overlay + fallback SVG behind for graceful
 *    degradation when an image URL fails.
 */
export default function TimeBackground({ overlay = 0.62, children, image }) {
  const autoMode = !image;
  const [tod, setTod] = useState(getTimeOfDay());
  // Two layers we alternate between for cross-fading.
  const [layers, setLayers] = useState(() => {
    const src = image || TIME_BACKGROUNDS[getTimeOfDay()];
    return [{ id: 0, src, visible: true }, { id: 1, src, visible: false }];
  });
  const toggleRef = useRef(0);

  // Auto time-of-day polling (every 60s), cross-fade when it changes.
  useEffect(() => {
    if (!autoMode) return;
    const iv = setInterval(() => {
      const next = getTimeOfDay();
      setTod((prev) => (prev !== next ? next : prev));
    }, 60_000);
    return () => clearInterval(iv);
  }, [autoMode]);

  // Whenever the src-of-truth changes, cross-fade the hidden layer in.
  useEffect(() => {
    const targetSrc = image || TIME_BACKGROUNDS[tod];
    setLayers((prev) => {
      const visible = prev.find((l) => l.visible);
      if (visible?.src === targetSrc) return prev;
      const nextIdx = toggleRef.current === 0 ? 1 : 0;
      toggleRef.current = nextIdx;
      return prev.map((l, i) => ({
        ...l,
        src: i === nextIdx ? targetSrc : l.src,
        visible: i === nextIdx,
      }));
    });
  }, [image, tod]);

  return (
    <div className="relative min-h-screen overflow-hidden">
      {/* Dark base always underneath (true fallback) */}
      <div
        className="fixed inset-0 z-0"
        style={{
          background:
            "radial-gradient(ellipse at top, rgba(50,20,45,1) 0%, rgba(10,5,16,1) 70%)",
          backgroundImage: "url(/fallback-scene.svg)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        aria-hidden
      />
      {/* Cross-fading scene layers */}
      {layers.map((l) => (
        <div
          key={l.id}
          className="scene-layer ken-burns z-0"
          style={{
            backgroundImage: `url(${l.src})`,
            opacity: l.visible ? 1 : 0,
          }}
          aria-hidden
        />
      ))}
      {/* Dark overlay */}
      <div
        className="fixed inset-0 z-0"
        style={{
          background: `linear-gradient(180deg, rgba(10,5,16,${Math.max(0, overlay - 0.1)}) 0%, rgba(10,5,16,${overlay}) 45%, rgba(10,5,16,${Math.min(1, overlay + 0.18)}) 100%)`,
        }}
        aria-hidden
      />
      <div className="relative z-10">{children}</div>
    </div>
  );
}
